<template>
  <div :class="{ 'zur-tenant': isZurTenant }">
    <Card
      v-if="state === 'Choice'"
      :footerDivider="false"
      :loadingState="loading"
      :title="$t('choice.title')"
    >
      <template v-slot:content>
        <p class="checkbox-area-margin">{{ $t('choice.content') }}</p>
        <Checkbox
          v-model="checkedSms"
          @change="checkboxChangedSms"
          id="sms-checkbox"
          class="mt-32"
          isRadio
          styleOption="secondary"
        >
          <template v-slot:label>
            <div class="checkbox-label-wrapper">
              <span class="icon-handy_outline icon-checkbox"></span
              ><span class="text">per SMS</span>
            </div>
          </template>
          <template v-slot:error>Error</template>
        </Checkbox>

        <template v-if="checkedSms">
          <div class="phoneNumber">
            <DialCodeSelect
              :allowedDialCodes="allowedDialCodes"
              :label="$t('choice.phoneNumber.dialCodeLabel')"
              v-model="dialCodeValue"
              showFlagInTrigger
              class="phoneNumber-dialCode"
            />

            <BasicTextfield
              :label="$t('choice.phoneNumber.label')"
              :placeholder="$t('choice.phoneNumber.placeholder')"
              :status="phoneNumberStatus"
              :value="phoneNumberValue"
              class="phoneNumber-input"
              @input="phoneNumberInput"
            >
              <template v-slot:error>
                {{ $t('choice.phoneNumber.error') }}
              </template>
              <template v-slot:info>
                <div v-dompurify-html="$t('choice.phoneNumber.info')"></div>
              </template>
            </BasicTextfield>
          </div>
        </template>

        <Checkbox
          v-model="checkedLetter"
          @change="checkboxChangedLetter"
          id="letter-checkbox"
          class="mt-16"
          isRadio
          styleOption="secondary"
        >
          <template v-slot:label>
            <CheckboxPostfach></CheckboxPostfach>
          </template>
          <template v-slot:error>Error</template>
        </Checkbox>
      </template>

      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            @click="requestUnlockCodeSwitch"
            :button-color="ButtonColor.PRIMARY"
            :disabledState="!isRequestEnabled"
          >
            {{
              checkedLetter
                ? $t('choice.buttonRequestLetter')
                : $t('choice.buttonRequestSms')
            }}
          </CustomButton>

          <CustomButton
            @click="
              () => (loggedIn ? gotoOverview() : gotoSuccessPageWithoutUnlockCode())
            "
            :button-color="ButtonColor.LINK"
            :disabledState="checkedSms || checkedLetter"
          >
            {{ $t('choice.buttonContinue') }}
          </CustomButton>
        </div>
      </template>
    </Card>

    <AddressConfirmation
      v-if="state === RequestUnlockCodeState.ADDRESS"
      :accountInformationLoggedOut="accountInformationLoggedOut()"
      :radioStyleOption="radioStyleOption"
      @onAddressConfirmationStateChanged="onAddressConfirmationStateChanged"
    ></AddressConfirmation>

    <Card
      v-if="state === 'UnlockCodeExists'"
      :footerDivider="false"
      :loadingState="loading"
      :title="$t('unlockCodeExists.title')"
    >
      <template v-slot:content>
        {{ $t('unlockCodeExists.content', { unlockCodeDate }) }}
      </template>

      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton @click="gotoUnlockCodeEntry" :button-color="ButtonColor.PRIMARY">
            {{ $t('unlockCodeExists.buttonRequest') }}
          </CustomButton>

          <CustomButton @click="gotoUnlockCodeSms" :button-color="ButtonColor.LINK">
            {{ $t('unlockCodeExists.buttonLink') }}
          </CustomButton>
        </div>
      </template>
    </Card>

    <UpgradeIdLevel
      v-if="state === 'EntryUnlockCode'"
      :accountId="accountId"
      :pukSmsResult="pukSmsResult"
      :username="username"
      @gotoChoice="gotoChoice"
    ></UpgradeIdLevel>

    <PukConfirmed
      v-if="state === 'SendUnlockCodeLetterSuccessAddressChange'"
      :addressChange="true"
      :username="username"
    ></PukConfirmed>

    <PukConfirmed
      v-if="state === 'ActivationSuccessWithoutUnlockCode'"
      :username="username"
    ></PukConfirmed>

    <Card
      v-if="state === 'SendUnlockCodeLetterSuccess'"
      :footerDivider="false"
      :title="$t('sendUnlockCodeLetterSuccess.title')"
    >
      <template v-slot:content>
        {{ $t('sendUnlockCodeLetterSuccess.content') }}
      </template>

      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            v-if="loggedIn"
            @click="gotoOverview"
            :buttonColor="ButtonColor.LINK"
          >
            {{ $t('sendUnlockCodeLetterSuccess.buttonLoggedIn') }}
          </CustomButton>
          <CustomButton
            v-if="!loggedIn"
            :button-color="ButtonColor.PRIMARY"
            @click.prevent="gotoLogin"
          >
            {{ $t('sendUnlockCodeLetterSuccess.buttonLoggedOut') }}
          </CustomButton>
        </div>
      </template>
    </Card>

    <Card
      v-if="state === 'ErrorLetter'"
      :footerDivider="false"
      :title="$t('errorLetter.title')"
    >
      <template v-slot:content>
        <div v-dompurify-html="$t('errorLetter.content')"></div>
      </template>

      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            @click="gotoSettings"
            style="padding-left: 0"
            :button-color="ButtonColor.LINK"
          >
            {{ $t('buttonBackLetter') }}
          </CustomButton>
        </div>
      </template>
    </Card>

    <Card
      v-if="state === 'ErrorSmsOffline'"
      :footerDivider="false"
      :title="$t('errorSmsOffline.title')"
    >
      <template v-slot:content>
        <div
          v-if="loggedIn"
          v-dompurify-html="$t('errorSmsOffline.contentLoggedIn')"
        ></div>
        <div
          v-if="!loggedIn"
          v-dompurify-html="$t('errorSmsOffline.contentLoggedOut')"
        ></div>
      </template>

      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            :button-color="ButtonColor.PRIMARY"
            @click.prevent="showAddressConfirmation"
          >
            {{ $t('errorSmsOffline.buttonGotoChoiceUnlockcode') }}
          </CustomButton>

          <CustomButton
            :button-color="ButtonColor.LINK"
            @click.prevent="
              () => (loggedIn ? gotoOverview() : gotoSuccessPageWithoutUnlockCode())
            "
          >
            {{
              loggedIn
                ? $t('errorSmsOffline.gotoOverview')
                : $t('errorSmsOffline.gotoSuccessPage')
            }}
          </CustomButton>
        </div>
      </template>
    </Card>

    <Card
      v-if="state === 'ErrorSmsExceed'"
      :footerDivider="false"
      :title="$t('errorSmsExceed.title')"
    >
      <template v-slot:content>
        <div
          v-if="loggedIn"
          v-dompurify-html="$t('errorSmsExceed.contentLoggedIn')"
        ></div>
        <div
          v-if="!loggedIn"
          v-dompurify-html="$t('errorSmsExceed.contentLoggedOut')"
        ></div>
      </template>

      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            @click="showAddressConfirmation"
            :button-color="ButtonColor.PRIMARY"
          >
            {{ $t('errorSmsExceed.buttonGotoChoiceUnlockcode') }}
          </CustomButton>

          <CustomButton
            v-if="loggedIn"
            :button-color="ButtonColor.LINK"
            @click.prevent="gotoOverview"
          >
            {{ $t('errorSmsExceed.gotoOverview') }}
          </CustomButton>

          <CustomButton
            v-if="!loggedIn"
            :button-color="ButtonColor.SECONDARY"
            @click.prevent="gotoLogin"
          >
            {{ $t('errorSmsExceed.gotoLogin') }}
          </CustomButton>
        </div>
      </template>
    </Card>

    <Card
      v-if="state === RequestUnlockCodeState.MISSING_PARTNER_ADDRESS_ID"
      :footerDivider="false"
      :title="$t('missingPartnerAddressId.title')"
    >
      <template v-slot:content>
        <div v-dompurify-html="$t('missingPartnerAddressId.content')"></div>
      </template>

      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            @click="gotoSettings"
            style="padding-left: 0"
            :button-color="ButtonColor.LINK"
          >
            {{ $t('buttonBackLetter') }}
          </CustomButton>
        </div>
      </template>
    </Card>
  </div>
</template>

<script lang="ts" setup>
import {
  BasicTextfield,
  ButtonColor,
  Card,
  Checkbox,
  CustomButton,
  DialCodeSelect,
  InputStatus,
  StyleOption
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { getAccountId, isAuthenticated } from '@/utils/KeycloakUtils'
import UpgradeIdLevel from '../idlevelupgrade/UpgradeIdLevel.vue'
import PukConfirmed from '../idlevelupgrade/PukConfirmed.vue'
import AddressConfirmation from '../addressconfirmation/AddressConfirmation.vue'
import { computed, onMounted, type PropType, ref, type Ref, watch } from 'vue'
import { isZur } from '@/utils/CommonUtils'
import { useI18n } from 'vue-i18n'
import {
  ALLOWED_COUNTRY_PREFIXES,
  cleanPhoneNumber,
  isPhoneNumberValid,
  removeLeadingZeros
} from '@/utils/accountmanagement/AccountManagementUtils'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { getDateFormatISO8601 } from '@/utils/utils'
import CheckboxPostfach from '@/componentsTenant/account-management/CheckboxPostfach.vue'
import {
  type AccountInformationLoggedOut,
  type PukSmsResult,
  RequestUnlockCodeState
} from '@/types/account-management/AccountManagementTypes'
import {
  sendUnlockCodeBySmsLoggedIn,
  sendUnlockCodeBySmsLoggedOut
} from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'

dayjs.extend(customParseFormat)

const { t } = useI18n()

const isZurTenant = computed(() => isZur())

const props = defineProps({
  policyId: {
    type: String,
    required: false,
    default: ''
  },
  accountId: {
    type: String,
    required: false,
    default: ''
  },
  birthdate: {
    type: String,
    required: false,
    default: ''
  },
  username: {
    type: String,
    required: false
  },
  radioStyleOption: {
    type: String as PropType<StyleOption>,
    default: StyleOption.PRIMARY
  }
})
const loading = ref(false)
const state = ref(RequestUnlockCodeState.CHOICE)
const unlockCodeDate: Ref<string | undefined> = ref(undefined)
const loggedIn = ref(false)
const checkedSms = ref(false)
const checkedLetter = ref(false)
const phoneNumberValue = ref('')
const phoneNumberStatus = ref<InputStatus>(InputStatus.DEFAULT)
const pukSmsResult: Ref<PukSmsResult | undefined> = ref(undefined)

const allowedDialCodes = ALLOWED_COUNTRY_PREFIXES.map((p) => `+${p}`)
const defaultDialCode = computed(() => t('choice.phoneNumber.value'))
const dialCodeValue = ref(defaultDialCode.value)
const fullPhoneNumber = computed(() => dialCodeValue.value + phoneNumberValue.value)

const countriesWithLeadingZeros = new Set(['39', '378'])

const normalizePhoneNumberForDialCode = (
  input: string,
  dialCode: string = dialCodeValue.value
): string => {
  const digitsOnly = cleanPhoneNumber(input).replaceAll('+', '')
  const currentDialCode = dialCode.replace('+', '')
  const allowsLeadingZeros = countriesWithLeadingZeros.has(currentDialCode)

  return allowsLeadingZeros ? digitsOnly : removeLeadingZeros(digitsOnly)
}

const updatePhoneNumberStatus = (): void => {
  phoneNumberStatus.value =
    phoneNumberValue.value !== '' && isPhoneNumberValid(fullPhoneNumber.value)
      ? InputStatus.VALID
      : InputStatus.ERROR
}

watch(dialCodeValue, () => {
  if (phoneNumberValue.value !== '') {
    phoneNumberValue.value = normalizePhoneNumberForDialCode(phoneNumberValue.value)
    updatePhoneNumberStatus()
  }
})

onMounted(async () => {
  loggedIn.value = isAuthenticated()
})

const accountInformationLoggedOut = (): AccountInformationLoggedOut | undefined => {
  return props.accountId !== '' && props.birthdate !== ''
    ? {
        policyId: props.policyId,
        accountId: props.accountId,
        birthdate: props.birthdate
      }
    : undefined
}

const isRequestEnabled = computed((): boolean => {
  return (
    (checkedSms.value &&
      phoneNumberStatus.value === InputStatus.VALID &&
      !checkedLetter.value) ||
    (!checkedSms.value && checkedLetter.value)
  )
})

const checkboxChangedSms = computed(() => (checked: boolean): void => {
  if (checked && checkedLetter.value) {
    checkedLetter.value = false
  }
})

const checkboxChangedLetter = computed(() => (checked: boolean): void => {
  if (checked && checkedSms.value) {
    checkedSms.value = false
  }
})

const phoneNumberInput = (input: string): void => {
  phoneNumberValue.value = normalizePhoneNumberForDialCode(input)
  updatePhoneNumberStatus()
}

const requestUnlockCodeSwitch = (): void => {
  if (checkedSms.value) {
    sendUnlockCodeSms()
  } else {
    showAddressConfirmation()
  }
}

const showAddressConfirmation = (): void => {
  state.value = RequestUnlockCodeState.ADDRESS
}

const onAddressConfirmationStateChanged = (
  _state: RequestUnlockCodeState,
  _unlockCodeDate?: string
): void => {
  state.value = _state
  if (state.value === RequestUnlockCodeState.UNLOCK_CODE_EXISTS && unlockCodeDate)
    unlockCodeDate.value = _unlockCodeDate
  if (state.value === RequestUnlockCodeState.CHOICE) gotoUnlockCodeSms()
}

const gotoChoice = (): void => {
  state.value = RequestUnlockCodeState.CHOICE
  phoneNumberValue.value = ''
  phoneNumberStatus.value = InputStatus.DEFAULT
  dialCodeValue.value = defaultDialCode.value
}

const retrieveAccountId = () => {
  const informationLoggedOut = accountInformationLoggedOut()
  return loggedIn.value ? getAccountId() : informationLoggedOut?.accountId
}

function getFormattedBirthdate() {
  let formattedBirthdate = ''
  const accLoggedOutInfo = accountInformationLoggedOut()
  if (!loggedIn.value && accLoggedOutInfo) {
    formattedBirthdate = getDateFormatISO8601(accLoggedOutInfo.birthdate)
  }
  return formattedBirthdate
}

const setRequestUnlockCodeState = (
  stateValue: RequestUnlockCodeState,
  resultValue: PukSmsResult | undefined
): void => {
  state.value = stateValue
  pukSmsResult.value = resultValue
}

const sendUnlockCodeSms = async () => {
  phoneNumberValue.value = normalizePhoneNumberForDialCode(phoneNumberValue.value)
  updatePhoneNumberStatus()

  if (phoneNumberStatus.value !== InputStatus.VALID) {
    return
  }

  loading.value = true
  const accountId = retrieveAccountId()

  if (!accountId) {
    setRequestUnlockCodeState(RequestUnlockCodeState.ERROR_SMS_OFFLINE, undefined)
    loading.value = false
    return
  }

  try {
    const formattedBirthdate = getFormattedBirthdate()
    if (!loggedIn.value && formattedBirthdate) {
      await sendUnlockCodeBySmsLoggedOut(
        accountId,
        fullPhoneNumber.value,
        formattedBirthdate
      )
    } else {
      await sendUnlockCodeBySmsLoggedIn(accountId, fullPhoneNumber.value)
    }
    setRequestUnlockCodeState(RequestUnlockCodeState.ENTRY_UNLOCKCODE, {
      phoneNumber: fullPhoneNumber.value,
      date: dayjs().format('DD.MM.YYYY'),
      time: dayjs().format('HH:mm')
    })
  } catch (e) {
    if (e instanceof ResponseError) {
      const body: { code?: string } | undefined = await e.response
        .json()
        .catch(() => undefined)

      if (body?.code === 'E400-010-AMS') {
        setRequestUnlockCodeState(RequestUnlockCodeState.ERROR_SMS_PHONENUMBER, undefined)
      } else if (body?.code === 'E400-011-AMS') {
        setRequestUnlockCodeState(RequestUnlockCodeState.ERROR_SMS_EXCEED, undefined)
      } else {
        setRequestUnlockCodeState(RequestUnlockCodeState.ERROR_SMS_OFFLINE, undefined)
      }
    } else {
      setRequestUnlockCodeState(RequestUnlockCodeState.ERROR_SMS_OFFLINE, undefined)
    }
  } finally {
    loading.value = false
  }
}

const gotoSettings = (): void => {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_PROFIL_PAGE)
}

const gotoOverview = (): void => {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_LOGIN_ON_SUCCESS_REDIRECT)
}

const gotoLogin = (): void => {
  // username ablegen, um damit die Keycloak-Login-Maske vorzubefüllen
  localStorage.setItem('loginHint', props.username ?? '')

  // setzen einer spezifischen redirectUri, um sofort zur Überblick-Seite zu kommen
  const redirectUrl = (
    globalThis.location.origin +
    globalThis.ENV_CONFIG?.BASE_URL +
    globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_LOGIN_ON_SUCCESS_REDIRECT
  ).replaceAll(/([^:]\/)\/+/g, '$1')
  localStorage.setItem('redirectUri', redirectUrl)

  navigate(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE)
}

const gotoSuccessPageWithoutUnlockCode = (): void => {
  state.value = RequestUnlockCodeState.ACTIVATION_SUCCCESS_WITHOUT_UNLOCKCODE
}

const gotoUnlockCodeSms = (): void => {
  phoneNumberValue.value = ''
  dialCodeValue.value = defaultDialCode.value
  checkedLetter.value = false
  checkedSms.value = true
  state.value = RequestUnlockCodeState.CHOICE
}

const gotoUnlockCodeEntry = (): void => {
  state.value = RequestUnlockCodeState.ENTRY_UNLOCKCODE
}
</script>

<style lang="scss" scoped>
.highlight {
  font-family: var(--request-unlock-code-highlight), serif;
  color: var(--primary-color-0);
}

.with_margin_bottom_baseline {
  margin-bottom: var(--baseline);
}

.with_margin_bottom_baseline_double {
  margin-bottom: calc(var(--baseline) * 2);
}

.icon-checkbox {
  font-size: 32px !important;
}

.checkbox-area-margin {
  margin-bottom: 32px;
}

.checkbox-label-wrapper {
  display: table;
  margin-top: -4px;
}

.checkbox-label-wrapper span {
  vertical-align: middle;
  display: table-cell;
}

.checkbox-label-wrapper > span.text,
.checkbox-label-wrapper span.text {
  padding-left: 7px;
  font-size: 16px !important;
}

.phoneNumber {
  gap: 24px;
  flex-wrap: nowrap;
  align-items: flex-start;
  display: flex;
  margin-top: 7px;
  max-width: 600px;
  padding-bottom: 1px;
}

.phoneNumber-dialCode {
  flex: 0 0 180px;
}

.phoneNumber-input {
  flex: 1 1 auto;
  min-width: 0;
}

@media (max-width: 768px) {
  .phoneNumber {
    flex-direction: column;
    gap: 0;
    padding-bottom: 0;
    margin-bottom: 0;
  }

  .phoneNumber-dialCode {
    flex: initial;
    width: 100%;
  }

  .phoneNumber-input {
    flex: initial;
    width: 100%;
  }

  .phoneNumber :deep(.field) {
    margin-bottom: 0;
  }

  .phoneNumber :deep(.field-body) {
    margin-bottom: 0;
  }

  .phoneNumber :deep(.control) {
    padding-bottom: 0;
  }
}

.zur-tenant :deep(label.radio__label::after) {
  top: 3px !important;
}

.zur-tenant :deep(label.radio__label::before) {
  top: 3px !important;
}
</style>
